/**
 * 重大资产域（R）契约 —— **R1–R8 · 38 AC 的唯一形状来源**
 *
 * 依据：`DR-053`（D1–D22 全生命周期闭环）· `R域PRD` **v1.3**（§4 载体与口径 / §5 功能 / §6 AC）·
 * 规格 §9（重大资产）/ §12（净资产第 9 类）· `DR-036`（金额必带币种）· `DR-044`（期限块可空）。
 *
 * ## 🔴 四条形状纪律
 *
 * | # | 纪律 | 为什么 |
 * |:--:|---|---|
 * | 1 | **金额一律整数分 + 币种同现** | `DR-036` D1：`Amount.currency` 必填；本域的钱都带 `currency` |
 * | 2 | 🔴 **回报四数由服务端算好下发** | `AC-R7.2`：端侧自算 = 违规。`roiE4` 是**万分之一整数**（定点，`money.ts` 白名单） |
 * | 3 | 🔴 **未估值 = `null`，不是 0** | `AC-R2.1` / `AC-R7.4`：`estimatedValue=null` ⇒ 回报区「待估值」、不进净资产 |
 * | 4 | **资金动作的金额只出现在请求里** | `AC-R6.6`：落库只进腿，响应里也不回"累计金额副本"以外的任何东西 —— 累计数是**现算**的派生值 |
 *
 * ## ⚠️ 本域**不**定义金额汇总表
 *
 * 累计投入 / 累计收益 / 实得全部由 `legs`（`linked_major_asset_id`）现算 ⇒
 * 删掉一笔交易，口径**自动回滚**（`AC-R8.5`）。任何"把汇总落到业务表"的做法都会让这条失效。
 */

import { z } from 'zod';
import { DayString } from './pagination.js';
import { RepaymentMethod } from '../enums/account.js';
import type { MajorAssetReturnResult } from '../nucleus/major-asset.js';

// ─────────────────────────── 枚举（3 类 / 2 法） ───────────────────────────

/** 类型：`property` 房产 / `vehicle` 车辆 / `collectible` 其他收藏 —— 🔴 **不新增第 4 类**（`AC-R1.1`） */
export const MajorAssetType = {
  Property: 'property',
  Vehicle: 'vehicle',
  Collectible: 'collectible',
} as const;
export type MajorAssetType = (typeof MajorAssetType)[keyof typeof MajorAssetType];

/** 折旧方法：`straight` 直线法 / `none` 不折旧（`DR-053` D7） */
export const DepMethod = {
  Straight: 'straight',
  None: 'none',
} as const;
export type DepMethod = (typeof DepMethod)[keyof typeof DepMethod];

/** 收益分类**唯一映射**（`DR-053` D13 · `AC-R6.4`：其他分类一律拒绝） */
export const MAJOR_ASSET_INCOME_CATEGORY_IDS = ['cat-rent-income', 'cat-invest-gain'] as const;

// ─────────────────────────── 输入：台账 ───────────────────────────

const CentsInput = z
  .number({ message: '金额必须是数字（整数「分」）' })
  .int('金额必须是整数「分」（规格 §22.1）')
  .nonnegative('金额不得为负 —— 冲减请用对应动作，不要传负数');

const NameInput = z.string().trim().min(1, '名称不得为空').max(50, '名称最长 50 字');

/** 币种：ISO 4217 三位大写（与 `DR-004` 的多币种口径一致） */
const CurrencyInput = z.string().trim().regex(/^[A-Z]{3}$/, '币种须为 3 位大写 ISO 代码');

/**
 * `POST /api/major-assets` —— 新建（`AC-R1.2`：**最小必填 = 类型 + 名称 + 归属人**）。
 *
 * 🔴 其余字段全部可空、可后补 —— **不因缺项拦截**（"先把资产记下来，估值回头再填"是真实用法）。
 */
export const CreateMajorAssetRequestSchema = z.object({
  type: z.enum([MajorAssetType.Property, MajorAssetType.Vehicle, MajorAssetType.Collectible]),
  name: NameInput,
  /** 🔴 归属人 = 家庭成员（`DR-053` D5；**不是** `contra` 那种系统自动） */
  ownerId: z.string().trim().min(1, '请选择归属人'),
  location: z.string().trim().max(100).nullish(),
  /** 面积（平方厘米，避免浮点） */
  area: z.number().int().nonnegative().nullish(),
  purchaseCost: CentsInput.nullish(),
  purchaseDate: DayString.nullish(),
  currency: CurrencyInput.optional(),
  estimatedValue: CentsInput.nullish(),
  valuationDate: DayString.nullish(),
  depMethod: z.enum([DepMethod.Straight, DepMethod.None]).optional(),
  depYears: z.number().int().positive('折旧年限须为正整数').nullish(),
  note: z.string().trim().max(500).nullish(),
});
export type CreateMajorAssetRequest = z.infer<typeof CreateMajorAssetRequestSchema>;

/**
 * `PATCH /api/major-assets/:id` —— 编辑（🔴 **整组替换** · `AC-R1.4`）。
 *
 * 「整组替换」= 客户端把**当前全量**预填后再提交 ⇒ 只改一个字段时其余**原样带回**。
 * ⚠️ 这不是"部分更新"：漏传的字段会被重置为 `null` —— 这正是 `AC-R1.4` 要钉的行为
 * （反例：如果做成"只更新传了的字段"，用户清空某字段就永远清不掉）。
 */
export const UpdateMajorAssetRequestSchema = CreateMajorAssetRequestSchema.extend({
  hidden: z.boolean().optional(),
});
export type UpdateMajorAssetRequest = z.infer<typeof UpdateMajorAssetRequestSchema>;

/** `POST /api/major-assets/:id/loans` —— 设置关联贷款（**整组替换**，多对多） */
export const SetMajorAssetLoansRequestSchema = z.object({
  contraIds: z.array(z.string().trim().min(1)).max(20, '单个资产最多关联 20 笔贷款'),
});
export type SetMajorAssetLoansRequest = z.infer<typeof SetMajorAssetLoansRequestSchema>;

// ─────────────────────────── 输入：估值 ───────────────────────────

/**
 * `POST /api/major-assets/:id/valuations` —— 调整估值（**非交易动作** · `DR-053` D14）。
 *
 * 🔴 一次调用做两件事：更新资产当前估值/估值日 + **写一条估值历史**（`AC-R7.1`）。
 * 历史是估值曲线的唯一数据源 ⇒ 不许"只改当前值不留痕"。
 */
export const AdjustValuationRequestSchema = z.object({
  estimatedValue: CentsInput,
  valuationDate: DayString,
  note: z.string().trim().max(200).nullish(),
});
export type AdjustValuationRequest = z.infer<typeof AdjustValuationRequestSchema>;

// ─────────────────────────── 输入：资金动作（R6 / R8） ───────────────────────────

/**
 * `POST /api/major-assets/:id/purchase` —— **购入**（`AC-R6.1` / `AC-R6.2`）。
 *
 * 🔴 **一笔交易两条腿**（首付 + 贷款本金）：现实是银行直付卖家、钱不过手 ⇒
 * 拆成「借入 + 购入」两笔会**虚增现金流**、和银行对账单对不上（`DR-053` D16 反例钉）。
 *
 * - `downPaymentCents` 省略/0 = **全款贷款**（首付腿不生成）
 * - `loanAccountId` = 用**已有** loan 账户；`newLoan` = 一键新建（两者互斥、都可空 = 无贷款）
 */
export const PurchaseMajorAssetRequestSchema = z
  .object({
    date: DayString,
    /** 购置总价（= 首付 + 贷款本金；服务端校验） */
    totalCents: CentsInput.positive('购置总价须大于 0'),
    downPaymentCents: CentsInput.optional(),
    /** 首付/全款的付款账户 */
    fromAccountId: z.string().trim().min(1, '请选择付款账户'),
    loanAccountId: z.string().trim().min(1).nullish(),
    /** 一键新建贷款账户（期限块六项全可空 —— `DR-044`） */
    newLoan: z
      .object({
        name: NameInput,
        counterparty: z.string().trim().max(50).nullish(),
        annualRateE4: z.number().int().nonnegative().nullish(),
        terms: z.number().int().positive().nullish(),
        startDate: DayString.nullish(),
        dueDate: DayString.nullish(),
        method: z.enum([RepaymentMethod.EqualInstallment, RepaymentMethod.EqualPrincipal]).nullish(),
      })
      .nullish(),
    note: z.string().trim().max(200).nullish(),
  })
  .refine((v) => v.loanAccountId === undefined || v.newLoan === undefined || v.newLoan === null, {
    message: '已有贷款账户与新建贷款只能选一个',
    path: ['newLoan'],
  });
export type PurchaseMajorAssetRequest = z.infer<typeof PurchaseMajorAssetRequestSchema>;

/** `POST /api/major-assets/:id/investments` —— 追加投资（`AC-R6.3`：不计收支） */
export const AppendInvestmentRequestSchema = z.object({
  date: DayString,
  amountCents: CentsInput.positive('金额须大于 0'),
  fromAccountId: z.string().trim().min(1, '请选择付款账户'),
  note: z.string().trim().max(200).nullish(),
});
export type AppendInvestmentRequest = z.infer<typeof AppendInvestmentRequestSchema>;

/** `POST /api/major-assets/:id/incomes` —— 记收益（`AC-R6.4`：**计收入**，分类二选一） */
export const RecordIncomeRequestSchema = z.object({
  date: DayString,
  amountCents: CentsInput.positive('金额须大于 0'),
  toAccountId: z.string().trim().min(1, '请选择收款账户'),
  categoryId: z.enum(MAJOR_ASSET_INCOME_CATEGORY_IDS),
  note: z.string().trim().max(200).nullish(),
});
export type RecordIncomeRequest = z.infer<typeof RecordIncomeRequestSchema>;

/**
 * `POST /api/major-assets/:id/dispose` —— 处置（出售 / 报废 · `AC-R8.1`–`R8.5`）。
 *
 * 🔴 `proceedsCents = 0` **合法**（报废）。收款子笔 `exclude_from_stats=1`：
 * 卖房款是**资产变现**，不是收入 —— 与购入不计支出严格对称（`DR-053` D22.1）。
 */
export const DisposeMajorAssetRequestSchema = z.object({
  date: DayString,
  proceedsCents: CentsInput,
  /** 收款账户（`proceedsCents > 0` 时必填 —— 服务端判，契约不猜） */
  toAccountId: z.string().trim().min(1).nullish(),
  /** 一并结清关联贷款（调 Q 域提前结清，不新建还款路径） */
  settleLoans: z.boolean().optional(),
  note: z.string().trim().max(200).nullish(),
});
export type DisposeMajorAssetRequest = z.infer<typeof DisposeMajorAssetRequestSchema>;

// ─────────────────────────── 视图 ───────────────────────────

/**
 * 回报面板（`AC-R7.2` 的唯一形状）。
 *
 * 🔴 四数全部**服务端算好**（`modules/asset` + `money.ts` 定点），端侧只渲染。
 * 🔴 `state` 三态让「未估值」与「估值 0」在类型上就分开：
 * - `unvalued`：`estimatedValue = null` ⇒ 不显示回报率（不以 0 代入，`AC-R7.4`）
 * - `holding`：有估值、持有中
 * - `disposed`：已处置 ⇒ `finalPnlCents` 有值（最终回报锁定）
 *
 * ⚠️ 类型本体在**内核**（`nucleus/major-asset.ts`）—— 架构断言禁止内核反向引用契约，
 * 故这里只做再导出（对外形状不变）。
 */
export type MajorAssetReturnView = MajorAssetReturnResult;

/** 资产台账行（列表 & 详情共用） */
export interface MajorAssetView {
  id: string;
  type: MajorAssetType;
  name: string;
  location: string | null;
  area: number | null;
  purchaseCost: number | null;
  /** 🔴 `null` = 未填估值（禁补 0） */
  estimatedValue: number | null;
  /** 手填覆盖的累计折旧；`null` = 未填（`straight` 时由服务端派生） */
  depreciation: number | null;
  /** 账面净值 = 购置成本 − 累计折旧；两者任一为 `null` 时 `null`（`AC-R2.3`，仅展示） */
  bookValueCents: number | null;
  purchaseDate: string | null;
  currency: string;
  valuationDate: string | null;
  depMethod: DepMethod;
  depYears: number | null;
  note: string | null;
  hidden: boolean;
  /** 非空 = 已处置（退出净资产估值桶） */
  disposedDate: string | null;
  ownerId: string;
  ownerName: string;
  /** 关联贷款笔数（列表行要显示） */
  loanCount: number;
  returnView: MajorAssetReturnView;
  createdAt: number;
  updatedAt: number;
}

/** 估值历史行（估值曲线的数据源 —— **只用真实点**） */
export interface MajorAssetValuationView {
  id: string;
  value: number;
  currency: string;
  valuationDate: string;
  note: string | null;
  createdAt: number;
}

/** 关联贷款一行（剩余本金由 **Q 域** 出数，本域不自己算） */
export interface MajorAssetLoanView {
  accountId: string;
  name: string;
  /** 剩余本金（整数分）—— 取自 Q 域 `contra` 账户余额口径 */
  remainingCents: number;
  currency: string;
}

/** 详情 = 台账行 + 关联贷款 + 估值历史 + 净权益提示 */
export interface MajorAssetDetailView extends MajorAssetView {
  loans: MajorAssetLoanView[];
  /** 关联贷款剩余合计 */
  loanRemainingCents: number;
  /** 🔴 净权益提示 = 估值 − 关联剩余；**仅提示**，资产侧不扣减（`AC-R3.4`） */
  netEquityCents: number | null;
  valuations: MajorAssetValuationView[];
}

export interface MajorAssetListResult {
  items: MajorAssetView[];
  /** 已处置的（列表折叠区，只读） */
  disposed: MajorAssetView[];
}

/** 资金动作的响应：只回「生成了哪笔交易」+ 更新后的详情（金额不落业务表） */
export interface MajorAssetActionView {
  transactionId: string;
  /** 该动作影响到的交易 id 列表（购入可能同时建账户/计划表，但**只有一笔交易**） */
  createdAccountId: string | null;
  asset: MajorAssetDetailView;
}

/** 删除响应 */
export interface MajorAssetDeletedView {
  deleted: true;
  id: string;
}
